import React from "react";
import { Card } from "@/components/ui/card";
import { Store, Banknote } from "lucide-react";

export function SalesSummary({ summary, shopPaymentSummary }) {
  if (!summary) {
    return (
      <div className="text-sm text-muted-foreground mb-4" data-testid="sales-summary-loading">
        Loading summary…
      </div>
    );
  }
  const retail = summary.retail || { total: 0, count: 0, by_mode: {}, by_mode_count: {}, collections: {}, collections_total: 0 };
  const wholesale = summary.wholesale || { total: 0, count: 0, by_mode: {}, by_mode_count: {}, collections: {}, collections_total: 0 };
  const collectionsTotal = Number(summary.collections_total || 0);
  // Use sales_by_mode (GROSS sales payments) for Payment Modes card, fallback to by_mode for backward compatibility
  const allModes = summary.sales_by_mode || summary.by_mode || {};
  
  return (
    <div className="space-y-4 mb-6" data-testid="sales-summary-container">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="sales-summary">
        <Card className="p-5 bg-[#FAF7F4]">
          <div className="text-sm uppercase tracking-widest text-muted-foreground mb-1">Sales total</div>
          <div className="font-mono-data text-3xl font-semibold text-[#9E6D5B]" data-testid="summary-total">
            ₹{Number(summary.total || 0).toFixed(2)}
          </div>
          <div className="text-sm text-muted-foreground mt-1">{summary.count} sales</div>
          {collectionsTotal > 0 && (
            <div
              className="mt-3 pt-3 border-t border-[hsl(var(--border))]"
              data-testid="summary-collections"
            >
              <div className="text-sm uppercase tracking-widest text-muted-foreground">
                Credit collected
              </div>
              <div className="font-mono-data text-lg font-semibold text-emerald-700">
                + ₹{collectionsTotal.toFixed(2)}
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">
                From past credit sales
              </div>
            </div>
          )}
        </Card>
        
        {/* Retail - Total Only */}
        <Card className="p-5" data-testid="summary-retail">
          <div className="text-sm uppercase tracking-widest text-muted-foreground mb-1">Retail</div>
          <div className="font-mono-data text-2xl font-semibold text-green-700">
            ₹{Number(retail.total || 0).toFixed(2)}
          </div>
          <div className="text-sm text-muted-foreground mt-1">{retail.count || 0} sales</div>
        </Card>

        {/* Wholesale - Total Only */}
        <Card className="p-5" data-testid="summary-wholesale">
          <div className="text-sm uppercase tracking-widest text-muted-foreground mb-1">Wholesale</div>
          <div className="font-mono-data text-2xl font-semibold text-blue-700">
            ₹{Number(wholesale.total || 0).toFixed(2)}
          </div>
          <div className="text-sm text-muted-foreground mt-1">{wholesale.count || 0} sales</div>
        </Card>

        {/* Payment Modes - Common Totals */}
        <Card className="p-5">
          <div className="text-sm uppercase tracking-widest text-muted-foreground mb-2">Payment Modes</div>
          <div className="space-y-1.5 text-xs">
            {[
              { key: "cash", label: "CASH", color: "text-amber-700" },
              { key: "upi", label: "UPI", color: "text-purple-700" },
              { key: "card", label: "CARD", color: "text-cyan-700" },
              { key: "credit", label: "CREDIT", color: "text-orange-700" },
            ].map(({ key, label, color }) => (
              <div key={key} className="flex justify-between items-center">
                <span className="text-muted-foreground">{label}</span>
                <span className={`font-mono-data font-semibold ${color}`}>
                  ₹{Number(allModes[key] || 0).toFixed(2)}
                </span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Shop-wise Payment/Advance Received Breakdown */}
      {shopPaymentSummary && shopPaymentSummary.length > 0 && (
        <Card className="p-5" data-testid="shop-payment-breakdown">
          <div className="flex items-center gap-2 mb-4">
            <Store className="w-4 h-4 text-[#9E6D5B]" />
            <div className="text-sm uppercase tracking-widest text-muted-foreground font-semibold">
              Shop-wise Money Received
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {shopPaymentSummary.map((shop) => (
              <div
                key={shop.shop_id}
                className="p-4 rounded-lg border bg-muted/20"
                data-testid={`shop-payment-${shop.shop_id}`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="font-semibold text-sm">{shop.shop_name || "Unknown"}</div>
                  <div className="font-mono-data text-lg font-bold text-[#9E6D5B]">
                    ₹{Number(shop.total_received || 0).toFixed(2)}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="flex flex-col">
                    <span className="text-muted-foreground uppercase tracking-wide text-[10px]">By Mode</span>
                    <div className="space-y-1 mt-1">
                      {shop.combined_by_mode?.cash > 0 && (
                        <div className="flex items-center gap-1.5">
                          <Banknote className="w-3 h-3 text-green-600" />
                          <span className="text-muted-foreground">Cash:</span>
                          <span className="font-mono-data font-medium">₹{shop.combined_by_mode.cash.toFixed(2)}</span>
                        </div>
                      )}
                      {shop.combined_by_mode?.upi > 0 && (
                        <div className="flex items-center gap-1.5">
                          <span className="text-purple-600 text-[10px] font-bold">UPI</span>
                          <span className="font-mono-data font-medium">₹{shop.combined_by_mode.upi.toFixed(2)}</span>
                        </div>
                      )}
                      {shop.combined_by_mode?.card > 0 && (
                        <div className="flex items-center gap-1.5">
                          <span className="text-blue-600 text-[10px] font-bold">Card</span>
                          <span className="font-mono-data font-medium">₹{shop.combined_by_mode.card.toFixed(2)}</span>
                        </div>
                      )}
                      {!shop.combined_by_mode?.cash && !shop.combined_by_mode?.upi && !shop.combined_by_mode?.card && (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-muted-foreground uppercase tracking-wide text-[10px]">Breakdown</span>
                    <div className="space-y-1 mt-1">
                      {shop.total_advances > 0 && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Advances:</span>
                          <span className="font-mono-data text-emerald-700">₹{shop.total_advances.toFixed(2)}</span>
                        </div>
                      )}
                      {shop.total_payments > 0 && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Credit Payments:</span>
                          <span className="font-mono-data text-blue-700">₹{shop.total_payments.toFixed(2)}</span>
                        </div>
                      )}
                      {shop.total_other_income > 0 && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Other Income:</span>
                          <span className="font-mono-data text-indigo-700">₹{shop.total_other_income.toFixed(2)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
